import React, { useCallback, useEffect, useMemo, useState } from 'react';
import adminService from '../services/adminService';
import { useAuth } from '../contexts/AuthContext';
import {
  CRYSTAL_PROFILE_CONFIG_KEY,
  CRYSTAL_PROFILE_MODES,
  DEFAULT_CRYSTAL_PROFILE_CATALOG,
  normalizeCrystalProfileCatalog,
} from '../utils/crystalProfiles';
import './AdminConsole.css';

const statusColors = {
  healthy: '#16a34a',
  warning: '#f59e0b',
  critical: '#dc2626',
  error: '#dc2626',
  unknown: '#64748b',
};

const sectionTitleMap = {
  steps: 'Step Visibility',
  global: 'Global',
  background: 'Background Removal',
  depth: 'Depth Generation',
  pointcloud: 'Point Cloud Generation',
  uv: 'UV Mapping',
  mesh_general: 'Mesh: General',
  mesh_grid: 'Mesh: Grid From Depth',
  mesh_ball: 'Mesh: Ball Pivoting',
  mesh_poisson: 'Mesh: Poisson',
};

const sectionOrder = [
  'steps',
  'global',
  'background',
  'depth',
  'pointcloud',
  'uv',
  'mesh_general',
  'mesh_grid',
  'mesh_ball',
  'mesh_poisson',
];

const boolFields = ['available', 'visible', 'locked', 'nullable'];
const DEPTH_ALGORITHM_OPTIONS = [
  { value: 'depth_anything_v2', label: 'Depth Anything V2' },
  { value: 'depth_anything_3', label: 'Depth Anything 3' },
  { value: 'depth_pro', label: 'Depth Pro (Metric)' },
];

const ui = {
  tab: (isActive) => ({
    padding: '8px 12px',
    border: isActive ? '2px solid #3b82f6' : '1px solid rgba(0,0,0,0.1)',
    background: isActive ? 'rgba(59,130,246,0.08)' : 'white',
    borderRadius: 8,
    cursor: 'pointer',
  }),
  btn: ({ variant = 'default' } = {}) => ({
    padding: '6px 10px',
    borderRadius: 6,
    border: `1px solid ${variant === 'primary' ? '#2563eb' : 'rgba(0,0,0,0.15)'}`,
    background: variant === 'primary' ? '#2563eb' : 'white',
    color: variant === 'primary' ? 'white' : '#111827',
    cursor: 'pointer',
  }),
  th: {
    textAlign: 'left',
    padding: 8,
    position: 'sticky',
    top: 0,
    background: '#fff',
    borderBottom: '1px solid #e5e7eb',
    fontSize: 12,
  },
};

const StatusPill = ({ status }) => {
  const color = statusColors[status] || statusColors.unknown;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 8px',
        borderRadius: 999,
        background: `${color}1a`,
        color,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'capitalize',
      }}
    >
      {status || 'unknown'}
    </span>
  );
};

const MetricsCard = ({ title, status, children }) => (
  <div
    style={{
      border: '1px solid #e2e8f0',
      borderRadius: 10,
      padding: 14,
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <strong style={{ fontSize: 14 }}>{title}</strong>
      {status && <StatusPill status={status} />}
    </div>
    {children}
  </div>
);

const MetricRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
    <span style={{ color: '#64748b' }}>{label}</span>
    <span style={{ fontWeight: 600, color: '#111827' }}>{value ?? '—'}</span>
  </div>
);

const formatBytes = (value) => {
  if (!Number.isFinite(value)) return '—';
  if (value === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = value;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  return `${size.toFixed(size >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
};

const arraysEqual = (a = [], b = []) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
};

const normalizeNumber = (value) => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return null;
  }
  return Number(value);
};

const parseTokenInput = (value, numeric) => {
  if (Array.isArray(value)) {
    const parsed = value
      .map((entry) => (numeric ? Number(entry) : String(entry).trim()))
      .filter((entry) => (numeric ? !Number.isNaN(entry) : entry.length > 0));
    return parsed;
  }

  const raw = String(value || '').trim();
  if (!raw) return [];

  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
    .map((entry) => (numeric ? Number(entry) : entry))
    .filter((entry) => (numeric ? !Number.isNaN(entry) : true));
};

const safeParseJson = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return null;
  }
  if (typeof rawValue === 'object') {
    return rawValue;
  }
  try {
    return JSON.parse(String(rawValue));
  } catch (error) {
    return null;
  }
};

const cloneCrystalCatalog = (catalog) => {
  try {
    return JSON.parse(JSON.stringify(catalog));
  } catch (error) {
    return normalizeCrystalProfileCatalog(DEFAULT_CRYSTAL_PROFILE_CATALOG);
  }
};

const createDefaultCrystalPreset = (mode) => {
  if (mode === 'threeD') {
    return {
      id: `block_${Date.now()}`,
      label: 'New 3D Preset',
      shape: 'rectangle',
      widthMm: 50,
      heightMm: 50,
      depthMm: 20,
      dpi: 600,
    };
  }
  if (mode === 'accessories') {
    return {
      id: `acc_${Date.now()}`,
      label: 'New Accessories Preset',
      shape: 'accessory',
      widthMm: 40,
      heightMm: 30,
      dpi: 600,
    };
  }
  return {
    id: `rect_${Date.now()}`,
    label: 'New 2D Preset',
    shape: 'rectangle',
    widthMm: 50,
    heightMm: 50,
    dpi: 600,
  };
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read PNG file.'));
    reader.readAsDataURL(file);
  });

const loadImageFromDataUrl = (dataUrl) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Unable to decode uploaded PNG.'));
    image.src = dataUrl;
  });

const extractShapeGeometryFromPng = async (file) => {
  const dataUrl = await readFileAsDataUrl(file);
  if (!dataUrl.startsWith('data:image/png;base64,')) {
    throw new Error('Only PNG uploads are supported.');
  }

  const image = await loadImageFromDataUrl(dataUrl);
  const width = Math.max(1, image.naturalWidth || image.width || 1);
  const height = Math.max(1, image.naturalHeight || image.height || 1);

  let bounds = { width, height };
  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (context) {
      context.drawImage(image, 0, 0, width, height);
      const { data } = context.getImageData(0, 0, width, height);
      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;

      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const alpha = data[(y * width + x) * 4 + 3];
          if (alpha > 0) {
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }
      }

      if (maxX >= minX && maxY >= minY) {
        bounds = {
          width: Math.max(1, maxX - minX + 1),
          height: Math.max(1, maxY - minY + 1),
        };
      }
    }
  } catch (error) {
    // Fallback to full image bounds when alpha scan fails.
  }

  return {
    dataUrl,
    shapeRatio: Number((bounds.width / bounds.height).toFixed(6)),
    shapeBoundsPx: bounds,
  };
};

const normalizeClipMaskFromPng = async (file) => {
  const dataUrl = await readFileAsDataUrl(file);
  if (!dataUrl.startsWith('data:image/png;base64,')) {
    throw new Error('Only PNG uploads are supported.');
  }

  const image = await loadImageFromDataUrl(dataUrl);
  const width = Math.max(1, image.naturalWidth || image.width || 1);
  const height = Math.max(1, image.naturalHeight || image.height || 1);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    throw new Error('Unable to process clip mask PNG.');
  }

  context.clearRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  const imageData = context.getImageData(0, 0, width, height);
  const pixels = imageData.data;
  let keepPixelCount = 0;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const red = pixels[index];
      const green = pixels[index + 1];
      const blue = pixels[index + 2];
      const alpha = pixels[index + 3];
      const luminance = red * 0.299 + green * 0.587 + blue * 0.114;
      const keep = alpha > 8 && luminance >= 128;

      pixels[index] = 255;
      pixels[index + 1] = 255;
      pixels[index + 2] = 255;
      pixels[index + 3] = keep ? 255 : 0;

      if (keep) {
        keepPixelCount += 1;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (!keepPixelCount) {
    throw new Error('Clip mask has no keep area. Use white keep pixels and black/transparent remove pixels.');
  }

  context.putImageData(imageData, 0, 0);
  const normalizedDataUrl = canvas.toDataURL('image/png');
  const keepBounds =
    maxX >= minX && maxY >= minY
      ? {
          width: Math.max(1, maxX - minX + 1),
          height: Math.max(1, maxY - minY + 1),
        }
      : null;

  return {
    dataUrl: normalizedDataUrl,
    keepBoundsPx: keepBounds,
    keepRatio: keepBounds ? Number((keepBounds.width / keepBounds.height).toFixed(6)) : null,
  };
};

const TokenEditor = ({ tokens = [], numeric = false, placeholder = 'Add value', onChange, touched, hasError = false }) => {
  const [input, setInput] = useState('');

  const addToken = (rawToken) => {
    const value = String(rawToken || '').trim();
    if (!value) return;

    const parsed = numeric ? Number(value) : value;
    if (numeric && Number.isNaN(parsed)) {
      return;
    }

    if (tokens.includes(parsed)) {
      setInput('');
      return;
    }

    onChange([...tokens, parsed]);
    setInput('');
  };

  const removeToken = (idx) => {
    onChange(tokens.filter((_, index) => index !== idx));
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        border: hasError ? '1px solid #dc2626' : touched ? '1px solid #f59e0b' : '1px solid #cbd5e1',
        borderRadius: 6,
        padding: 6,
        minWidth: 180,
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {tokens.map((token, idx) => (
          <span
            key={`${token}-${idx}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: '#eff6ff',
              color: '#1e3a8a',
              border: '1px solid #bfdbfe',
              borderRadius: 999,
              padding: '2px 8px',
              fontSize: 12,
            }}
          >
            {String(token)}
            <button
              type="button"
              onClick={() => removeToken(idx)}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#1e3a8a' }}
              aria-label="remove token"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={input}
        placeholder={placeholder}
        onChange={(event) => setInput(event.target.value)}
        onBlur={() => addToken(input)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            addToken(input);
          }
        }}
        style={{ border: 'none', outline: 'none', fontSize: 12 }}
      />
    </div>
  );
};

const sectionFromParameter = (item) => {
  if (!item.step && item.key.startsWith('global.')) return 'global';
  if (item.step === 'background_removal') return 'background';
  if (item.step === 'depth_generation') return 'depth';
  if (item.step === 'pointcloud_generation') return 'pointcloud';
  if (item.step === 'uv_mapping') return 'uv';
  if (item.step === 'mesh_generation') {
    if (item.algorithm === 'grid_from_depth') return 'mesh_grid';
    if (item.algorithm === 'ball_pivoting') return 'mesh_ball';
    if (item.algorithm === 'poisson') return 'mesh_poisson';
    return 'mesh_general';
  }
  return 'mesh_general';
};

const AdminConsole = () => {
  const { user } = useAuth();

  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState('');
  const [activeTab, setActiveTab] = useState('parameters');

  const [globalError, setGlobalError] = useState('');

  const [paramList, setParamList] = useState([]);
  const [stepList, setStepList] = useState([]);
  const [paramFilter, setParamFilter] = useState('');
  const [selectedSection, setSelectedSection] = useState('steps');

  const [editorLoading, setEditorLoading] = useState(false);
  const [editorApplying, setEditorApplying] = useState(false);
  const [editorError, setEditorError] = useState('');
  const [editorResult, setEditorResult] = useState('');

  const [draftByKey, setDraftByKey] = useState({});
  const [dirtyByKey, setDirtyByKey] = useState({});
  const [stepDraftByCode, setStepDraftByCode] = useState({});
  const [dirtyStepByCode, setDirtyStepByCode] = useState({});
  const [batchFieldErrors, setBatchFieldErrors] = useState({});

  const [dbMat, setDbMat] = useState(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);

  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userTierFilter, setUserTierFilter] = useState('');
  const [userTierDrafts, setUserTierDrafts] = useState({});
  const [userSavingId, setUserSavingId] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '' });

  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState('');
  const [metricsData, setMetricsData] = useState({ health: null, performance: null, pool: null, fetchedAt: null });
  const [jobsMetricsLoading, setJobsMetricsLoading] = useState(false);
  const [jobsMetricsError, setJobsMetricsError] = useState('');
  const [jobsMetricsData, setJobsMetricsData] = useState({
    users: [],
    pagination: { page: 1, page_size: 50, total_count: 0, total_pages: 0 },
    status_filter: 'all',
    fetchedAt: null,
  });
  const [jobsMetricsSearch, setJobsMetricsSearch] = useState('');
  const [jobsMetricsTierFilter, setJobsMetricsTierFilter] = useState('');
  const [jobsMetricsStatusFilter, setJobsMetricsStatusFilter] = useState('all');
  const [jobsMetricsActiveOnly, setJobsMetricsActiveOnly] = useState('');
  const [jobsMetricsPage, setJobsMetricsPage] = useState(1);
  const [jobsStaleHours, setJobsStaleHours] = useState('48');
  const [jobsClearingKey, setJobsClearingKey] = useState('');
  const [crystalLoading, setCrystalLoading] = useState(false);
  const [crystalSaving, setCrystalSaving] = useState(false);
  const [crystalError, setCrystalError] = useState('');
  const [crystalResult, setCrystalResult] = useState('');
  const [crystalLoaded, setCrystalLoaded] = useState(false);
  const [crystalConfigUpdatedAt, setCrystalConfigUpdatedAt] = useState(null);
  const [crystalSavedCatalog, setCrystalSavedCatalog] = useState(() =>
    normalizeCrystalProfileCatalog(DEFAULT_CRYSTAL_PROFILE_CATALOG)
  );
  const [crystalDraftCatalog, setCrystalDraftCatalog] = useState(() =>
    normalizeCrystalProfileCatalog(DEFAULT_CRYSTAL_PROFILE_CATALOG)
  );
  const [activeCrystalMode, setActiveCrystalMode] = useState(CRYSTAL_PROFILE_MODES[0]?.mode || 'twoD');

  const resetEditorDrafts = useCallback(() => {
    setDraftByKey({});
    setDirtyByKey({});
    setStepDraftByCode({});
    setDirtyStepByCode({});
    setBatchFieldErrors({});
    setEditorError('');
    setEditorResult('');
  }, []);

  const loadTiers = useCallback(async () => {
    try {
      const list = await adminService.listTiers();
      setTiers(list);
      if (list.length > 0) {
        setSelectedTier((prev) => prev || list[0].code);
      }
    } catch (error) {
      setGlobalError('Unable to load tiers.');
    }
  }, []);

  const loadEditorData = useCallback(async (tierCode) => {
    if (!tierCode) return;
    setEditorLoading(true);
    setEditorError('');
    setEditorResult('');
    try {
      const [paramRes, stepRes] = await Promise.all([
        adminService.listTierParameters(tierCode),
        adminService.listTierSteps(tierCode),
      ]);
      setParamList(paramRes.items || []);
      setStepList(stepRes.steps || []);
      resetEditorDrafts();
    } catch (error) {
      setEditorError('Failed to load tier editor data.');
    } finally {
      setEditorLoading(false);
    }
  }, [resetEditorDrafts]);

  const loadUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const data = await adminService.listUsers({ page: 1, pageSize: 100 });
      const list = data.users || [];
      setUsers(list);
      setUserTierDrafts((prev) => {
        const next = { ...prev };
        list.forEach((entry) => {
          next[entry.id] = prev[entry.id] ?? entry.tier ?? 'free';
        });
        return next;
      });
    } catch (error) {
      setGlobalError('Failed to load users.');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const loadMetrics = useCallback(async () => {
    setMetricsError('');
    try {
      setMetricsLoading(true);
      const [health, performance, pool] = await Promise.all([
        adminService.getMonitoringHealthDetailed(),
        adminService.getMonitoringMetrics(),
        adminService.getDatabasePoolStatus(),
      ]);
      setMetricsData({ health, performance, pool, fetchedAt: new Date().toISOString() });
    } catch (error) {
      setMetricsError('Failed to load metrics.');
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  const loadJobMetrics = useCallback(async () => {
    setJobsMetricsError('');
    try {
      setJobsMetricsLoading(true);
      const data = await adminService.getUserJobMetrics({
        page: jobsMetricsPage,
        pageSize: 50,
        search: jobsMetricsSearch,
        tierFilter: jobsMetricsTierFilter,
        statusFilter: jobsMetricsStatusFilter,
        activeOnly: jobsMetricsActiveOnly === '' ? null : jobsMetricsActiveOnly === 'active',
      });
      setJobsMetricsData({
        users: data?.users || [],
        pagination: data?.pagination || { page: 1, page_size: 50, total_count: 0, total_pages: 0 },
        status_filter: data?.status_filter || jobsMetricsStatusFilter || 'all',
        fetchedAt: new Date().toISOString(),
      });
    } catch (error) {
      setJobsMetricsError('Failed to load job metrics.');
    } finally {
      setJobsMetricsLoading(false);
    }
  }, [
    jobsMetricsPage,
    jobsMetricsSearch,
    jobsMetricsTierFilter,
    jobsMetricsStatusFilter,
    jobsMetricsActiveOnly,
  ]);

  const jobsStaleHoursValue = useMemo(() => {
    const parsed = Number.parseInt(String(jobsStaleHours || '').trim(), 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return 48;
    }
    return parsed;
  }, [jobsStaleHours]);

  const loadCrystalConfig = useCallback(async () => {
    setCrystalError('');
    setCrystalResult('');
    try {
      setCrystalLoading(true);
      const configMap = await adminService.getSystemConfig();
      const entry = configMap?.[CRYSTAL_PROFILE_CONFIG_KEY];
      const parsedValue = safeParseJson(entry?.value);
      const normalized = normalizeCrystalProfileCatalog(
        parsedValue || DEFAULT_CRYSTAL_PROFILE_CATALOG
      );

      setCrystalSavedCatalog(normalized);
      setCrystalDraftCatalog(cloneCrystalCatalog(normalized));
      setCrystalConfigUpdatedAt(entry?.updated_at || null);

      if (entry && !parsedValue) {
        setCrystalError('Stored crystal config is invalid JSON. Showing normalized defaults until saved.');
      }
    } catch (error) {
      setCrystalError('Failed to load crystal profile configuration.');
    } finally {
      setCrystalLoaded(true);
      setCrystalLoading(false);
    }
  }, []);

  const refreshMaterialized = useCallback(async (tierCode) => {
    if (!tierCode) return;
    try {
      setDiagnosticsLoading(true);
      const db = await adminService.getMaterialized(tierCode);
      setDbMat(db);
    } catch (error) {
      setGlobalError('Failed to load materialized config.');
    } finally {
      setDiagnosticsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTiers();
  }, [loadTiers]);

  useEffect(() => {
    if (!selectedTier || activeTab !== 'parameters') return;
    loadEditorData(selectedTier);
  }, [selectedTier, activeTab, loadEditorData]);

  useEffect(() => {
    if (activeTab === 'users' && users.length === 0) {
      loadUsers();
    }
    if (activeTab === 'metrics' && !metricsData.health && !metricsLoading) {
      loadMetrics();
    }
    if (activeTab === 'diagnostics' && !dbMat && !diagnosticsLoading && selectedTier) {
      refreshMaterialized(selectedTier);
    }
    if (activeTab === 'crystal' && !crystalLoading && !crystalLoaded) {
      loadCrystalConfig();
    }
  }, [
    activeTab,
    users.length,
    metricsData.health,
    metricsLoading,
    dbMat,
    diagnosticsLoading,
    crystalLoading,
    crystalLoaded,
    selectedTier,
    loadUsers,
    loadMetrics,
    refreshMaterialized,
    loadCrystalConfig,
  ]);

  useEffect(() => {
    if (activeTab !== 'jobs') {
      return;
    }
    loadJobMetrics();
  }, [activeTab, loadJobMetrics]);

  const filteredParams = useMemo(() => {
    const q = paramFilter.trim().toLowerCase();
    if (!q) return paramList;
    return paramList.filter((item) => {
      return (
        String(item.key || '').toLowerCase().includes(q) ||
        String(item.step || '').toLowerCase().includes(q) ||
        String(item.algorithm || '').toLowerCase().includes(q)
      );
    });
  }, [paramFilter, paramList]);

  const paramsBySection = useMemo(() => {
    const grouped = {
      global: [],
      background: [],
      depth: [],
      pointcloud: [],
      uv: [],
      mesh_general: [],
      mesh_grid: [],
      mesh_ball: [],
      mesh_poisson: [],
    };

    filteredParams.forEach((item) => {
      const sectionId = sectionFromParameter(item);
      if (!grouped[sectionId]) grouped[sectionId] = [];
      grouped[sectionId].push(item);
    });

    return grouped;
  }, [filteredParams]);

  const sectionEntries = useMemo(() => {
    return sectionOrder
      .map((id) => {
        if (id === 'steps') {
          return {
            id,
            title: sectionTitleMap[id],
            kind: 'steps',
            items: stepList,
          };
        }

        return {
          id,
          title: sectionTitleMap[id],
          kind: 'parameters',
          items: paramsBySection[id] || [],
        };
      })
      .filter((entry) => entry.kind === 'steps' || entry.items.length > 0);
  }, [paramsBySection, stepList]);

  useEffect(() => {
    if (!sectionEntries.length) return;
    const exists = sectionEntries.some((entry) => entry.id === selectedSection);
    if (!exists) {
      setSelectedSection(sectionEntries[0].id);
    }
  }, [sectionEntries, selectedSection]);

  const buildParamPatch = (item, overrides = {}) => {
    const effective = item.effective || {};
    const dtype = item.data_type || '';

    const hasOverride = (field) => Object.prototype.hasOwnProperty.call(overrides, field);
    const readBool = (field, fallback) => (hasOverride(field) ? !!overrides[field] : !!fallback);
    const readNumber = (field, fallback) => {
      const source = hasOverride(field) ? overrides[field] : fallback;
      if (source === '' || source === null || source === undefined) return null;
      const parsed = Number(source);
      return Number.isNaN(parsed) ? null : parsed;
    };

    const readText = (field, fallback = '') => {
      if (hasOverride(field)) {
        return overrides[field] ?? '';
      }
      return fallback ?? '';
    };

    const readList = (field, fallback = [], numeric = false) => {
      if (hasOverride(field)) {
        return parseTokenInput(overrides[field], numeric);
      }
      return parseTokenInput(fallback, numeric);
    };

    const patch = {
      available: readBool('available', effective.available),
      visible: readBool('visible', effective.visible),
      locked: readBool('locked', effective.locked),
      nullable: readBool('nullable', effective.nullable),
    };

    if (dtype === 'boolean') {
      patch.default_boolean = readBool('default_boolean', effective.default_boolean);
    } else if (dtype === 'number') {
      patch.default_number = readNumber('default_number', effective.default_number);
      patch.min_number = readNumber('min_number', effective.min_number);
      patch.max_number = readNumber('max_number', effective.max_number);
    } else if (dtype === 'select') {
      patch.default_text = readText('default_text', effective.default_text);
      patch.allowed_values = readList('allowed_values', effective.allowed_values || [], false);
    } else if (dtype.startsWith('array')) {
      const isNumeric = dtype.startsWith('array:number');
      const defaultFallback = effective.default_array || effective.default || [];
      patch.default_array = readList('default_array', defaultFallback, isNumeric);
      patch.allowed_values = readList('allowed_values', effective.allowed_values || [], isNumeric);
    }

    return patch;
  };

  const paramPatchChanged = (item, patch) => {
    const effective = item.effective || {};

    if (boolFields.some((field) => patch[field] !== !!effective[field])) {
      return true;
    }

    if ('default_boolean' in patch && patch.default_boolean !== !!effective.default_boolean) {
      return true;
    }

    if ('default_number' in patch && normalizeNumber(patch.default_number) !== normalizeNumber(effective.default_number)) {
      return true;
    }
    if ('min_number' in patch && normalizeNumber(patch.min_number) !== normalizeNumber(effective.min_number)) {
      return true;
    }
    if ('max_number' in patch && normalizeNumber(patch.max_number) !== normalizeNumber(effective.max_number)) {
      return true;
    }

    if ('default_text' in patch && String(patch.default_text || '') !== String(effective.default_text || '')) {
      return true;
    }

    if ('default_array' in patch) {
      const baseArray = effective.default_array || effective.default || [];
      if (!arraysEqual(patch.default_array || [], baseArray || [])) {
        return true;
      }
    }

    if ('allowed_values' in patch && !arraysEqual(patch.allowed_values || [], effective.allowed_values || [])) {
      return true;
    }

    return false;
  };

  const updateParamDraft = (item, field, value) => {
    const existingDraft = draftByKey[item.key] || {};
    const nextDraft = { ...existingDraft, [field]: value };

    const patch = buildParamPatch(item, nextDraft);
    const isDirty = paramPatchChanged(item, patch);

    setDraftByKey((prev) => ({ ...prev, [item.key]: nextDraft }));
    setDirtyByKey((prev) => {
      const next = { ...prev };
      if (isDirty) {
        next[item.key] = true;
      } else {
        delete next[item.key];
      }
      return next;
    });

    setBatchFieldErrors((prev) => {
      const next = { ...prev };
      Object.keys(next)
        .filter((k) => k.startsWith(`parameter:${item.key}:`))
        .forEach((k) => delete next[k]);
      return next;
    });
  };

  const buildStepPatch = (step, overrides = {}) => {
    const effective = step.effective || {};
    const mapped = {
      available: 'available',
      visible: 'visible',
      default_enabled: 'enabled_by_default',
      locked: 'locked',
      pipeline_step_order: 'pipeline_step_order',
    };

    const patch = {};
    Object.entries(mapped).forEach(([localField, apiField]) => {
      if (!Object.prototype.hasOwnProperty.call(overrides, localField)) return;
      const localValue = overrides[localField];
      const effectiveField = localField === 'default_enabled' ? 'default_enabled' : localField;
      const currentValue = effective[effectiveField];

      if (localField === 'pipeline_step_order') {
        const parsed = localValue === '' || localValue === null || localValue === undefined ? null : Number(localValue);
        const normalizedCurrent =
          currentValue === '' || currentValue === null || currentValue === undefined
            ? null
            : Number(currentValue);
        if (parsed !== normalizedCurrent) {
          patch[apiField] = parsed;
        }
      } else if (!!localValue !== !!currentValue) {
        patch[apiField] = !!localValue;
      }
    });

    return patch;
  };

  const updateStepDraft = (step, field, value) => {
    const existingDraft = stepDraftByCode[step.code] || {};
    const nextDraft = { ...existingDraft, [field]: value };
    const patch = buildStepPatch(step, nextDraft);
    const isDirty = Object.keys(patch).length > 0;

    setStepDraftByCode((prev) => ({ ...prev, [step.code]: nextDraft }));
    setDirtyStepByCode((prev) => {
      const next = { ...prev };
      if (isDirty) {
        next[step.code] = true;
      } else {
        delete next[step.code];
      }
      return next;
    });

    setBatchFieldErrors((prev) => {
      const next = { ...prev };
      Object.keys(next)
        .filter((k) => k.startsWith(`step:${step.code}:`))
        .forEach((k) => delete next[k]);
      return next;
    });
  };

  const discardParamRow = (itemKey) => {
    setDraftByKey((prev) => {
      const next = { ...prev };
      delete next[itemKey];
      return next;
    });
    setDirtyByKey((prev) => {
      const next = { ...prev };
      delete next[itemKey];
      return next;
    });
    setBatchFieldErrors((prev) => {
      const next = { ...prev };
      Object.keys(next)
        .filter((k) => k.startsWith(`parameter:${itemKey}:`))
        .forEach((k) => delete next[k]);
      return next;
    });
  };

  const discardStepRow = (stepCode) => {
    setStepDraftByCode((prev) => {
      const next = { ...prev };
      delete next[stepCode];
      return next;
    });
    setDirtyStepByCode((prev) => {
      const next = { ...prev };
      delete next[stepCode];
      return next;
    });
    setBatchFieldErrors((prev) => {
      const next = { ...prev };
      Object.keys(next)
        .filter((k) => k.startsWith(`step:${stepCode}:`))
        .forEach((k) => delete next[k]);
      return next;
    });
  };

  const discardSection = (sectionId) => {
    if (sectionId === 'steps') {
      stepList.forEach((step) => discardStepRow(step.code));
      return;
    }

    const items = paramsBySection[sectionId] || [];
    items.forEach((item) => discardParamRow(item.key));
  };

  const discardAll = () => {
    resetEditorDrafts();
  };

  const dirtyBySection = useMemo(() => {
    const counts = {};
    sectionEntries.forEach((entry) => {
      counts[entry.id] = 0;
    });

    Object.keys(dirtyByKey).forEach((paramKey) => {
      if (!dirtyByKey[paramKey]) return;
      const item = paramList.find((entry) => entry.key === paramKey);
      if (!item) return;
      const sectionId = sectionFromParameter(item);
      counts[sectionId] = (counts[sectionId] || 0) + 1;
    });

    Object.keys(dirtyStepByCode).forEach((stepCode) => {
      if (!dirtyStepByCode[stepCode]) return;
      counts.steps = (counts.steps || 0) + 1;
    });

    return counts;
  }, [dirtyByKey, dirtyStepByCode, paramList, sectionEntries]);

  const totalDirtyCount = useMemo(() => {
    return Object.values(dirtyBySection).reduce((sum, count) => sum + count, 0);
  }, [dirtyBySection]);

  const selectedSectionEntry = sectionEntries.find((entry) => entry.id === selectedSection) || null;

  const parseBatchValidationErrors = (payload) => {
    if (!payload || typeof payload !== 'object') return null;
    const detail = payload?.detail && typeof payload.detail === 'object' ? payload.detail : payload;
    if (detail.code !== 'BATCH_VALIDATION_FAILED' || !Array.isArray(detail.errors)) return null;

    const errorMap = {};
    detail.errors.forEach((entry) => {
      const targetType = entry.target_type || 'parameter';
      const targetKey = entry.target_key || '';
      const field = entry.field || '';
      const mapKey = `${targetType}:${targetKey}:${field}`;
      errorMap[mapKey] = entry.message || 'Validation failed';
    });

    return errorMap;
  };

  const rowErrors = (targetType, targetKey) => {
    return Object.entries(batchFieldErrors)
      .filter(([key]) => key.startsWith(`${targetType}:${targetKey}:`))
      .map(([, message]) => message);
  };

  const fieldError = (targetType, targetKey, field) => {
    return (
      batchFieldErrors[`${targetType}:${targetKey}:${field}`] ||
      batchFieldErrors[`${targetType}:${targetKey}:`] ||
      ''
    );
  };

  const checkboxStyle = (touched, errorMessage) => {
    if (errorMessage) {
      return { outline: '2px solid #dc2626' };
    }
    if (touched) {
      return { outline: '2px solid #f59e0b' };
    }
    return {};
  };

  const inputStyle = (touched, errorMessage, base = {}) => ({
    ...base,
    ...(errorMessage ? { border: '2px solid #dc2626' } : touched ? { border: '2px solid #f59e0b' } : {}),
  });

  const preflightClientValidation = () => {
    const validationErrors = {};

    paramList
      .filter((item) => dirtyByKey[item.key])
      .forEach((item) => {
        if (item.data_type !== 'number') return;

        const patch = buildParamPatch(item, draftByKey[item.key] || {});
        const minValue = normalizeNumber(patch.min_number);
        const maxValue = normalizeNumber(patch.max_number);
        const defaultValue = normalizeNumber(patch.default_number);

        if (minValue !== null && maxValue !== null && minValue > maxValue) {
          validationErrors[`parameter:${item.key}:min_number`] = 'min_number cannot exceed max_number';
        }
        if (defaultValue !== null && minValue !== null && defaultValue < minValue) {
          validationErrors[`parameter:${item.key}:default_number`] = 'default_number is below min_number';
        }
        if (defaultValue !== null && maxValue !== null && defaultValue > maxValue) {
          validationErrors[`parameter:${item.key}:default_number`] = 'default_number exceeds max_number';
        }
      });

    return validationErrors;
  };

  const applyStagedChanges = async () => {
    if (!selectedTier || totalDirtyCount === 0) return;

    const clientValidationErrors = preflightClientValidation();
    if (Object.keys(clientValidationErrors).length > 0) {
      setBatchFieldErrors(clientValidationErrors);
      setEditorError('Resolve highlighted validation issues before applying changes.');
      return;
    }

    setEditorApplying(true);
    setEditorError('');
    setEditorResult('');
    setBatchFieldErrors({});

    const parameterUpdates = paramList
      .filter((item) => dirtyByKey[item.key])
      .map((item) => ({
        param_key: item.key,
        patch: buildParamPatch(item, draftByKey[item.key] || {}),
      }));

    const stepUpdates = stepList
      .filter((step) => dirtyStepByCode[step.code])
      .map((step) => ({
        step_code: step.code,
        patch: buildStepPatch(step, stepDraftByCode[step.code] || {}),
      }));

    try {
      const result = await adminService.batchApplyTierConfig(selectedTier, {
        parameter_updates: parameterUpdates,
        step_updates: stepUpdates,
      });

      setEditorResult(
        `Applied ${result?.applied?.parameter_updates || 0} parameter updates and ${result?.applied?.step_updates || 0} step updates.`
      );
      await loadEditorData(selectedTier);
    } catch (error) {
      const parsed = parseBatchValidationErrors(error?.response?.data);
      if (parsed) {
        setBatchFieldErrors(parsed);
        setEditorError('Batch validation failed. Review highlighted rows and retry.');
      } else {
        setEditorError('Failed to apply staged changes.');
      }
    } finally {
      setEditorApplying(false);
    }
  };

  const onCreateUser = async () => {
    const { username, email, password } = newUser;
    if (!username || !email || !password) {
      setGlobalError('Please fill username, email and password.');
      return;
    }
    try {
      setUsersLoading(true);
      await adminService.createUser({ username, email, password });
      setNewUser({ username: '', email: '', password: '' });
      await loadUsers();
    } catch (error) {
      setGlobalError('Failed to create user.');
    } finally {
      setUsersLoading(false);
    }
  };

  const onUpdateUserTier = async (userId, newTier) => {
    try {
      setUserSavingId(userId);
      await adminService.updateUserTier(userId, newTier);
      await loadUsers();
    } catch (error) {
      setGlobalError('Failed to update user tier.');
    } finally {
      setUserSavingId(null);
    }
  };

  const onDeactivateUser = async (userId) => {
    try {
      setUserSavingId(userId);
      await adminService.deactivateUser(userId);
      await loadUsers();
    } catch (error) {
      setGlobalError('Failed to deactivate user.');
    } finally {
      setUserSavingId(null);
    }
  };

  const onReactivateUser = async (userId) => {
    try {
      setUserSavingId(userId);
      await adminService.reactivateUser(userId);
      await loadUsers();
    } catch (error) {
      setGlobalError('Failed to reactivate user.');
    } finally {
      setUserSavingId(null);
    }
  };

  const onUnlockUser = async (userId) => {
    try {
      setUserSavingId(userId);
      await adminService.unlockUser(userId);
      await loadUsers();
    } catch (error) {
      setGlobalError('Failed to unlock user.');
    } finally {
      setUserSavingId(null);
    }
  };

  const onRevokeSessions = async (userId) => {
    try {
      setUserSavingId(userId);
      await adminService.revokeUserSessions(userId);
      await loadUsers();
    } catch (error) {
      setGlobalError('Failed to revoke sessions.');
    } finally {
      setUserSavingId(null);
    }
  };

  const onClearUserJobs = async (userId, statuses, actionLabel) => {
    if (!userId || !Array.isArray(statuses) || statuses.length === 0) {
      return;
    }

    const clearKey = `${userId}:${statuses.join(',')}`;
    if (jobsClearingKey) {
      return;
    }

    const confirmed = window.confirm(`Clear ${actionLabel} jobs for this user? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    try {
      setJobsClearingKey(clearKey);
      setJobsMetricsError('');
      await adminService.clearUserJobs(userId, statuses);
      await loadJobMetrics();
    } catch (error) {
      setJobsMetricsError('Failed to clear user jobs.');
    } finally {
      setJobsClearingKey('');
    }
  };

  const onClearUserStaleProcessingJobs = async (userId) => {
    if (!userId) {
      return;
    }

    const staleHours = jobsStaleHoursValue;
    const clearKey = `${userId}:processing:${staleHours}`;
    if (jobsClearingKey) {
      return;
    }

    const confirmed = window.confirm(
      `Clear processing jobs older than ${staleHours} hour(s) for this user? Active running jobs are skipped.`
    );
    if (!confirmed) {
      return;
    }

    try {
      setJobsClearingKey(clearKey);
      setJobsMetricsError('');
      await adminService.clearUserStaleProcessingJobs(userId, staleHours);
      await loadJobMetrics();
    } catch (error) {
      setJobsMetricsError('Failed to clear stale processing jobs.');
    } finally {
      setJobsClearingKey('');
    }
  };

  const normalizedCrystalDraftCatalog = useMemo(
    () => normalizeCrystalProfileCatalog(crystalDraftCatalog),
    [crystalDraftCatalog]
  );

  const normalizedCrystalSavedCatalog = useMemo(
    () => normalizeCrystalProfileCatalog(crystalSavedCatalog),
    [crystalSavedCatalog]
  );

  const crystalCatalogDirty = useMemo(
    () => JSON.stringify(normalizedCrystalDraftCatalog) !== JSON.stringify(normalizedCrystalSavedCatalog),
    [normalizedCrystalDraftCatalog, normalizedCrystalSavedCatalog]
  );

  const crystalModeSections = useMemo(
    () =>
      CRYSTAL_PROFILE_MODES.map((definition) => ({
        ...definition,
        section: normalizedCrystalDraftCatalog?.[definition.mode],
      })),
    [normalizedCrystalDraftCatalog]
  );

  const activeCrystalModeSection = useMemo(() => {
    return crystalModeSections.find((entry) => entry.mode === activeCrystalMode) || crystalModeSections[0] || null;
  }, [activeCrystalMode, crystalModeSections]);
  const activeCrystalSection = activeCrystalModeSection?.section || null;

  useEffect(() => {
    if (!crystalModeSections.some((entry) => entry.mode === activeCrystalMode) && crystalModeSections.length > 0) {
      setActiveCrystalMode(crystalModeSections[0].mode);
    }
  }, [activeCrystalMode, crystalModeSections]);

  const updateCrystalDraft = (updater) => {
    setCrystalDraftCatalog((prev) => {
      const base = normalizeCrystalProfileCatalog(prev || DEFAULT_CRYSTAL_PROFILE_CATALOG);
      const mutable = cloneCrystalCatalog(base);
      const updated = updater(mutable) || mutable;
      return normalizeCrystalProfileCatalog(updated);
    });
    setCrystalError('');
    setCrystalResult('');
  };

  const updateCrystalPresetField = (mode, index, field, value) => {
    updateCrystalDraft((draft) => {
      const section = draft?.[mode];
      if (!section || !Array.isArray(section.presets) || !section.presets[index]) {
        return draft;
      }
      const nextValue = field === 'id' || field === 'label'
        ? String(value || '')
        : value;
      section.presets[index] = {
        ...section.presets[index],
        [field]: nextValue,
      };
      return draft;
    });
  };

  const updateCrystalPresetDimension = (mode, index, axis, rawValue) => {
    updateCrystalDraft((draft) => {
      const section = draft?.[mode];
      if (!section || !Array.isArray(section.presets) || !section.presets[index]) {
        return draft;
      }
      const preset = section.presets[index];
      const numeric = Number(rawValue);
      const nextValue = Number.isFinite(numeric) && numeric > 0 ? Number(numeric.toFixed(1)) : 1;
      const ratio = Number(preset.shapeRatio);
      const ratioValid = Number.isFinite(ratio) && ratio > 0;

      if (axis === 'width') {
        preset.widthMm = nextValue;
        if (ratioValid) {
          preset.heightMm = Number((nextValue / ratio).toFixed(1));
        }
      } else {
        preset.heightMm = nextValue;
        if (ratioValid) {
          preset.widthMm = Number((nextValue * ratio).toFixed(1));
        }
      }

      return draft;
    });
  };

  const updateCrystalPresetDepth = (mode, index, rawValue) => {
    updateCrystalDraft((draft) => {
      const section = draft?.[mode];
      if (!section || !Array.isArray(section.presets) || !section.presets[index]) {
        return draft;
      }
      const numeric = Number(rawValue);
      section.presets[index].depthMm = Number.isFinite(numeric) && numeric > 0 ? Number(numeric.toFixed(1)) : 1;
      return draft;
    });
  };

  const updateCrystalPresetDpi = (mode, index, rawValue) => {
    updateCrystalDraft((draft) => {
      const section = draft?.[mode];
      if (!section || !Array.isArray(section.presets) || !section.presets[index]) {
        return draft;
      }
      const numeric = Number(rawValue);
      section.presets[index].dpi =
        Number.isFinite(numeric) && numeric > 0
          ? Math.max(72, Math.min(1200, Math.round(numeric)))
          : 600;
      return draft;
    });
  };

  const uploadCrystalPresetShape = async (mode, index, file) => {
    if (!file) {
      return;
    }
    if (file.type !== 'image/png') {
      setCrystalError('Only PNG shape uploads are supported.');
      return;
    }
    try {
      const shape = await extractShapeGeometryFromPng(file);
      updateCrystalDraft((draft) => {
        const section = draft?.[mode];
        if (!section || !Array.isArray(section.presets) || !section.presets[index]) {
          return draft;
        }
        const preset = section.presets[index];
        const nextWidth = Number(preset.widthMm) || 50;
        const nextHeight = Number((nextWidth / shape.shapeRatio).toFixed(1));
        section.presets[index] = {
          ...preset,
          shapePngDataUrl: shape.dataUrl,
          shapeRatio: shape.shapeRatio,
          shapeBoundsPx: shape.shapeBoundsPx,
          widthMm: nextWidth,
          heightMm: nextHeight > 0 ? nextHeight : 1,
        };
        return draft;
      });
    } catch (error) {
      setCrystalError(error?.message || 'Unable to parse uploaded shape PNG.');
    }
  };

  const uploadCrystalPresetClipMask = async (mode, index, file) => {
    if (!file) {
      return;
    }
    if (file.type !== 'image/png') {
      setCrystalError('Only PNG clip-mask uploads are supported.');
      return;
    }
    try {
      const clipMask = await normalizeClipMaskFromPng(file);
      updateCrystalDraft((draft) => {
        const section = draft?.[mode];
        if (!section || !Array.isArray(section.presets) || !section.presets[index]) {
          return draft;
        }
        const preset = section.presets[index];
        section.presets[index] = {
          ...preset,
          clipMaskPngDataUrl: clipMask.dataUrl,
        };
        return draft;
      });
    } catch (error) {
      setCrystalError(error?.message || 'Unable to parse uploaded clip-mask PNG.');
    }
  };

  const clearCrystalPresetShape = (mode, index) => {
    updateCrystalDraft((draft) => {
      const section = draft?.[mode];
      if (!section || !Array.isArray(section.presets) || !section.presets[index]) {
        return draft;
      }
      const preset = section.presets[index];
      section.presets[index] = {
        ...preset,
        shapePngDataUrl: undefined,
        shapeRatio: undefined,
        shapeBoundsPx: undefined,
      };
      return draft;
    });
  };

  const clearCrystalPresetClipMask = (mode, index) => {
    updateCrystalDraft((draft) => {
      const section = draft?.[mode];
      if (!section || !Array.isArray(section.presets) || !section.presets[index]) {
        return draft;
      }
      const preset = section.presets[index];
      section.presets[index] = {
        ...preset,
        clipMaskPngDataUrl: undefined,
      };
      return draft;
    });
  };

  const setCrystalDefaultPreset = (mode, presetId) => {
    updateCrystalDraft((draft) => {
      const section = draft?.[mode];
      if (!section) {
        return draft;
      }
      section.defaultPresetId = presetId;
      return draft;
    });
  };

  const addCrystalPreset = (mode) => {
    updateCrystalDraft((draft) => {
      const section = draft?.[mode];
      if (!section || !Array.isArray(section.presets)) {
        return draft;
      }
      const nextPreset = createDefaultCrystalPreset(mode);
      section.presets.push(nextPreset);
      if (!section.defaultPresetId) {
        section.defaultPresetId = nextPreset.id;
      }
      return draft;
    });
  };

  const removeCrystalPreset = (mode, index) => {
    updateCrystalDraft((draft) => {
      const section = draft?.[mode];
      if (!section || !Array.isArray(section.presets) || !section.presets[index]) {
        return draft;
      }
      const removedId = section.presets[index].id;
      section.presets = section.presets.filter((_, rowIdx) => rowIdx !== index);
      if (section.presets.length === 0) {
        const seeded = createDefaultCrystalPreset(mode);
        section.presets = [seeded];
        section.defaultPresetId = seeded.id;
      } else if (section.defaultPresetId === removedId) {
        section.defaultPresetId = section.presets[0].id;
      }
      return draft;
    });
  };

  const saveCrystalCatalog = async () => {
    try {
      setCrystalSaving(true);
      setCrystalError('');
      setCrystalResult('');
      const normalized = normalizeCrystalProfileCatalog(crystalDraftCatalog);
      const response = await adminService.updateSystemConfig(
        CRYSTAL_PROFILE_CONFIG_KEY,
        JSON.stringify(normalized),
        'Crystal profile catalog for 2D, 3D, and accessories presets'
      );
      setCrystalSavedCatalog(normalized);
      setCrystalDraftCatalog(cloneCrystalCatalog(normalized));
      setCrystalConfigUpdatedAt(response?.updated_at || new Date().toISOString());
      setCrystalLoaded(true);
      setCrystalResult('Crystal profile catalog saved.');
    } catch (error) {
      setCrystalError('Failed to save crystal profile catalog.');
    } finally {
      setCrystalSaving(false);
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter((entry) => {
      const q = userSearch.trim().toLowerCase();
      if (q) {
        const match =
          String(entry.username || '').toLowerCase().includes(q) ||
          String(entry.email || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      if (userTierFilter) {
        return String(entry.tier || '').toLowerCase() === userTierFilter.toLowerCase();
      }
      return true;
    });
  }, [users, userSearch, userTierFilter]);

  if (!user?.is_admin) {
    return (
      <div>
        <h2>Admin Console</h2>
        <p>You need admin privileges to access this page.</p>
      </div>
    );
  }

  return (
    <div>
      <h2>Admin Console</h2>
      {globalError && <div style={{ color: '#b91c1c', marginBottom: 8 }}>{globalError}</div>}

      <section style={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>Admin Tools</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setActiveTab('parameters')} style={ui.tab(activeTab === 'parameters')}>Parameters</button>
            <button type="button" onClick={() => setActiveTab('crystal')} style={ui.tab(activeTab === 'crystal')}>Crystal</button>
            <button type="button" onClick={() => setActiveTab('users')} style={ui.tab(activeTab === 'users')}>Users</button>
            <button type="button" onClick={() => setActiveTab('jobs')} style={ui.tab(activeTab === 'jobs')}>Jobs</button>
            <button type="button" onClick={() => setActiveTab('metrics')} style={ui.tab(activeTab === 'metrics')}>Metrics</button>
            <button type="button" onClick={() => setActiveTab('diagnostics')} style={ui.tab(activeTab === 'diagnostics')}>Diagnostics</button>
          </div>
        </header>

        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {tiers.map((tier) => (
            <button
              key={tier.code}
              type="button"
              onClick={() => setSelectedTier(tier.code)}
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                border: selectedTier === tier.code ? '2px solid #3b82f6' : '1px solid rgba(0,0,0,0.15)',
                background: selectedTier === tier.code ? 'rgba(59,130,246,0.12)' : 'white',
              }}
            >
              {tier.code}
            </button>
          ))}
        </div>
      </section>

      {activeTab === 'parameters' && (
        <section className="admin-editor-shell" style={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8, padding: 16 }}>
          <header style={{ marginBottom: 10 }}>
            <h3 style={{ margin: 0 }}>Tier Editor V2</h3>
            <p style={{ color: '#64748b', margin: '6px 0 0 0' }}>
              Stage changes locally, review touched rows, then apply atomically to database tier configuration.
            </p>
          </header>

          <div className="admin-editor-layout">
            <aside className="admin-editor-left-pane">
              <div className="admin-editor-filter">
                <input
                  type="text"
                  placeholder="Filter parameters by key/step..."
                  value={paramFilter}
                  onChange={(event) => setParamFilter(event.target.value)}
                  className="admin-editor-filter-input"
                />
              </div>

              <div className="admin-editor-section-list">
                {sectionEntries.map((entry) => {
                  const dirtyCount = dirtyBySection[entry.id] || 0;
                  const isActive = selectedSection === entry.id;
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => setSelectedSection(entry.id)}
                      className={`admin-editor-section-button ${isActive ? 'is-active' : ''}`}
                    >
                      <div className="admin-editor-section-row">
                        <span className="admin-editor-section-title">{entry.title}</span>
                        <span className={`admin-editor-section-state ${dirtyCount > 0 ? 'is-dirty' : ''}`}>
                          {dirtyCount > 0 ? `${dirtyCount} unsaved` : 'clean'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>

            <div className="admin-editor-right-pane">
              <div className="admin-editor-right-toolbar">
                <div>
                  <strong>{selectedSectionEntry ? selectedSectionEntry.title : 'Select a section'}</strong>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                    {totalDirtyCount > 0 ? `${totalDirtyCount} total unsaved changes` : 'No staged changes'}
                  </div>
                </div>
                <div className="admin-editor-toolbar-actions">
                  <button
                    type="button"
                    onClick={() => loadEditorData(selectedTier)}
                    disabled={!selectedTier || editorLoading || editorApplying}
                    style={ui.btn({ variant: 'default' })}
                  >
                    {editorLoading ? 'Loading…' : 'Reload'}
                  </button>
                  <button
                    type="button"
                    onClick={() => discardSection(selectedSection)}
                    disabled={editorApplying || !selectedSectionEntry || (dirtyBySection[selectedSection] || 0) === 0}
                    style={ui.btn({ variant: 'default' })}
                  >
                    Discard Section
                  </button>
                  <button
                    type="button"
                    onClick={discardAll}
                    disabled={editorApplying || totalDirtyCount === 0}
                    style={ui.btn({ variant: 'default' })}
                  >
                    Discard All
                  </button>
                  <button
                    type="button"
                    onClick={applyStagedChanges}
                    disabled={editorApplying || totalDirtyCount === 0}
                    style={ui.btn({ variant: 'primary' })}
                  >
                    {editorApplying ? 'Applying…' : `Apply Changes (${totalDirtyCount})`}
                  </button>
                </div>
              </div>

              <div className="admin-editor-right-body">
                {editorError && <div style={{ color: '#b91c1c', marginBottom: 8 }}>{editorError}</div>}
                {editorResult && <div style={{ color: '#166534', marginBottom: 8 }}>{editorResult}</div>}

                {editorLoading ? (
                  <p style={{ color: '#64748b' }}>Loading editor data…</p>
                ) : !selectedSectionEntry ? (
                  <p style={{ color: '#64748b' }}>No section selected.</p>
                ) : selectedSectionEntry.kind === 'steps' ? (
                  <div className="admin-editor-table-wrap">
                    <table className="admin-editor-table admin-editor-steps-table">
                      <thead>
                        <tr>
                          <th className="admin-editor-table-head admin-editor-cell-key">Step</th>
                          <th className="admin-editor-table-head">Available</th>
                          <th className="admin-editor-table-head">Visible</th>
                          <th className="admin-editor-table-head">Default Enabled</th>
                          <th className="admin-editor-table-head">Locked</th>
                          <th className="admin-editor-table-head">Order</th>
                          <th className="admin-editor-table-head admin-editor-cell-status">Status</th>
                          <th className="admin-editor-table-head admin-editor-cell-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stepList.map((step) => {
                          const draft = stepDraftByCode[step.code] || {};
                          const dirty = !!dirtyStepByCode[step.code];
                          const rowErr = rowErrors('step', step.code);

                          const readStepValue = (field, fallback) => {
                            if (Object.prototype.hasOwnProperty.call(draft, field)) {
                              return draft[field];
                            }
                            return fallback;
                          };

                          const touched = (field) => Object.prototype.hasOwnProperty.call(draft, field);
                          const availableError = fieldError('step', step.code, 'available');
                          const visibleError = fieldError('step', step.code, 'visible');
                          const defaultEnabledError = fieldError('step', step.code, 'enabled_by_default');
                          const lockedError = fieldError('step', step.code, 'locked');
                          const stepOrderError = fieldError('step', step.code, 'pipeline_step_order');

                          return (
                            <tr key={step.code} className="admin-editor-table-row" style={{ background: dirty ? '#fff7ed' : 'transparent' }}>
                              <td className="admin-editor-cell admin-editor-cell-key">
                                <strong>{step.display_name}</strong>
                                <div style={{ color: '#64748b', fontSize: 12 }}>{step.code}</div>
                              </td>
                              <td className="admin-editor-cell">
                                <input
                                  type="checkbox"
                                  checked={!!readStepValue('available', step.effective.available)}
                                  onChange={(event) => updateStepDraft(step, 'available', event.target.checked)}
                                  style={checkboxStyle(touched('available'), availableError)}
                                />
                              </td>
                              <td className="admin-editor-cell">
                                <input
                                  type="checkbox"
                                  checked={!!readStepValue('visible', step.effective.visible)}
                                  onChange={(event) => updateStepDraft(step, 'visible', event.target.checked)}
                                  style={checkboxStyle(touched('visible'), visibleError)}
                                />
                              </td>
                              <td className="admin-editor-cell">
                                <input
                                  type="checkbox"
                                  checked={!!readStepValue('default_enabled', step.effective.default_enabled)}
                                  onChange={(event) => updateStepDraft(step, 'default_enabled', event.target.checked)}
                                  style={checkboxStyle(touched('default_enabled'), defaultEnabledError)}
                                />
                              </td>
                              <td className="admin-editor-cell">
                                <input
                                  type="checkbox"
                                  checked={!!readStepValue('locked', step.effective.locked)}
                                  onChange={(event) => updateStepDraft(step, 'locked', event.target.checked)}
                                  style={checkboxStyle(touched('locked'), lockedError)}
                                />
                              </td>
                              <td className="admin-editor-cell">
                                <input
                                  type="number"
                                  value={readStepValue('pipeline_step_order', step.effective.pipeline_step_order ?? '')}
                                  onChange={(event) => {
                                    const val = event.target.value;
                                    updateStepDraft(step, 'pipeline_step_order', val === '' ? null : Number(val));
                                  }}
                                  style={inputStyle(touched('pipeline_step_order'), stepOrderError, { width: 100 })}
                                />
                              </td>
                              <td className="admin-editor-cell admin-editor-cell-status">
                                <div style={{ color: dirty ? '#b45309' : '#64748b', fontSize: 12 }}>
                                  {dirty ? 'Unsaved' : 'Saved'}
                                </div>
                                {rowErr.map((msg, idx) => (
                                  <div key={idx} style={{ color: '#b91c1c', fontSize: 11 }}>{msg}</div>
                                ))}
                              </td>
                              <td className="admin-editor-cell admin-editor-cell-actions">
                                <button
                                  type="button"
                                  disabled={!dirty}
                                  onClick={() => discardStepRow(step.code)}
                                  style={ui.btn({ variant: 'default' })}
                                >
                                  Discard Row
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="admin-editor-table-wrap">
                    <table className="admin-editor-table admin-editor-params-table">
                      <thead>
                        <tr>
                          <th className="admin-editor-table-head admin-editor-cell-key">Key</th>
                          <th className="admin-editor-table-head">Type</th>
                          <th className="admin-editor-table-head">Available</th>
                          <th className="admin-editor-table-head">Visible</th>
                          <th className="admin-editor-table-head">Locked</th>
                          <th className="admin-editor-table-head">Default</th>
                          <th className="admin-editor-table-head">Min</th>
                          <th className="admin-editor-table-head">Max</th>
                          <th className="admin-editor-table-head">Nullable</th>
                          <th className="admin-editor-table-head">Allowed Values</th>
                          <th className="admin-editor-table-head admin-editor-cell-status">Status</th>
                          <th className="admin-editor-table-head admin-editor-cell-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSectionEntry.items.map((item) => {
                          const effective = item.effective || {};
                          const draft = draftByKey[item.key] || {};
                          const dirty = !!dirtyByKey[item.key];
                          const dtype = item.data_type || '';
                          const rowErr = rowErrors('parameter', item.key);

                          const hasDraft = (field) => Object.prototype.hasOwnProperty.call(draft, field);

                          const readBool = (field, fallback) => (hasDraft(field) ? !!draft[field] : !!fallback);
                          const readNum = (field, fallback) => {
                            if (hasDraft(field)) {
                              return draft[field];
                            }
                            return fallback === null || fallback === undefined ? '' : fallback;
                          };
                          const readText = (field, fallback = '') => (hasDraft(field) ? draft[field] : fallback ?? '');
                          const readList = (field, fallback = [], numeric = false) => {
                            if (hasDraft(field)) {
                              return parseTokenInput(draft[field], numeric);
                            }
                            return parseTokenInput(fallback, numeric);
                          };

                          const isNumericArray = dtype.startsWith('array:number');
                          const isNumericAllowedList = isNumericArray || dtype.startsWith('select:number');
                          const allowedValues = readList('allowed_values', effective.allowed_values || [], isNumericAllowedList);
                          const isDepthAlgorithmParam = item.key === 'depth_generation.algorithm';
                          const normalizedAllowedValues = allowedValues.map((opt) => String(opt));
                          const knownDepthAlgorithmSet = new Set(DEPTH_ALGORITHM_OPTIONS.map((opt) => opt.value));
                          const depthAlgorithmExtraValues = isDepthAlgorithmParam
                            ? normalizedAllowedValues.filter((opt) => !knownDepthAlgorithmSet.has(opt))
                            : [];
                          const defaultArrayValues = readList(
                            'default_array',
                            effective.default_array || effective.default || [],
                            isNumericArray
                          );

                          const defaultMin = typeof item.catalog?.min_number === 'number' ? item.catalog.min_number : undefined;
                          const defaultMax = typeof item.catalog?.max_number === 'number' ? item.catalog.max_number : undefined;
                          const defaultStep = typeof item.catalog?.step_number === 'number' ? item.catalog.step_number : undefined;

                          const defaultSelectOptions = allowedValues.length > 0 ? allowedValues : (effective.allowed_values || []);
                          const availableError = fieldError('parameter', item.key, 'available');
                          const visibleError = fieldError('parameter', item.key, 'visible');
                          const lockedError = fieldError('parameter', item.key, 'locked');
                          const defaultBoolError = fieldError('parameter', item.key, 'default_boolean');
                          const defaultNumberError = fieldError('parameter', item.key, 'default_number');
                          const defaultTextError = fieldError('parameter', item.key, 'default_text');
                          const defaultArrayError = fieldError('parameter', item.key, 'default_array');
                          const minNumberError = fieldError('parameter', item.key, 'min_number');
                          const maxNumberError = fieldError('parameter', item.key, 'max_number');
                          const nullableError = fieldError('parameter', item.key, 'nullable');
                          const allowedValuesError = fieldError('parameter', item.key, 'allowed_values');

                          return (
                            <tr key={item.key} className="admin-editor-table-row" style={{ background: dirty ? '#fff7ed' : 'transparent' }}>
                              <td className="admin-editor-cell admin-editor-cell-key admin-editor-cell-code">
                                {item.key}
                              </td>
                              <td className="admin-editor-cell">{dtype || '—'}</td>
                              <td className="admin-editor-cell">
                                <input
                                  type="checkbox"
                                  checked={readBool('available', effective.available)}
                                  onChange={(event) => updateParamDraft(item, 'available', event.target.checked)}
                                  style={checkboxStyle(hasDraft('available'), availableError)}
                                />
                              </td>
                              <td className="admin-editor-cell">
                                <input
                                  type="checkbox"
                                  checked={readBool('visible', effective.visible)}
                                  onChange={(event) => updateParamDraft(item, 'visible', event.target.checked)}
                                  style={checkboxStyle(hasDraft('visible'), visibleError)}
                                />
                              </td>
                              <td className="admin-editor-cell">
                                <input
                                  type="checkbox"
                                  checked={readBool('locked', effective.locked)}
                                  onChange={(event) => updateParamDraft(item, 'locked', event.target.checked)}
                                  style={checkboxStyle(hasDraft('locked'), lockedError)}
                                />
                              </td>
                              <td className="admin-editor-cell">
                                {dtype === 'boolean' ? (
                                  <input
                                    type="checkbox"
                                    checked={readBool('default_boolean', effective.default_boolean)}
                                    onChange={(event) => updateParamDraft(item, 'default_boolean', event.target.checked)}
                                    style={checkboxStyle(hasDraft('default_boolean'), defaultBoolError)}
                                  />
                                ) : dtype === 'number' ? (
                                  <input
                                    type="number"
                                    value={readNum('default_number', effective.default_number)}
                                    min={defaultMin}
                                    max={defaultMax}
                                    step={defaultStep || 'any'}
                                    onChange={(event) => updateParamDraft(item, 'default_number', event.target.value)}
                                    style={inputStyle(hasDraft('default_number'), defaultNumberError, { width: 110 })}
                                  />
                                ) : dtype === 'select' ? (
                                  <select
                                    value={readText('default_text', effective.default_text || '')}
                                    onChange={(event) => updateParamDraft(item, 'default_text', event.target.value)}
                                    disabled={defaultSelectOptions.length === 0}
                                    style={inputStyle(hasDraft('default_text'), defaultTextError, { width: 170 })}
                                  >
                                    <option value="">
                                      {defaultSelectOptions.length > 0 ? '-- select --' : 'Set allowed values first'}
                                    </option>
                                    {defaultSelectOptions.map((opt, idx) => (
                                      <option key={`${opt}-${idx}`} value={String(opt)}>{String(opt)}</option>
                                    ))}
                                  </select>
                                ) : dtype.startsWith('array') ? (
                                  <TokenEditor
                                    tokens={defaultArrayValues}
                                    numeric={isNumericArray}
                                    placeholder="Add default"
                                    touched={hasDraft('default_array') || !!defaultArrayError}
                                    hasError={!!defaultArrayError}
                                    onChange={(values) => updateParamDraft(item, 'default_array', values)}
                                  />
                                ) : (
                                  <span style={{ color: '#94a3b8' }}>—</span>
                                )}
                              </td>
                              <td className="admin-editor-cell">
                                {dtype === 'number' ? (
                                  <input
                                    type="number"
                                    value={readNum('min_number', effective.min_number)}
                                    onChange={(event) => updateParamDraft(item, 'min_number', event.target.value)}
                                    style={inputStyle(hasDraft('min_number'), minNumberError, { width: 90 })}
                                  />
                                ) : (
                                  <span style={{ color: '#94a3b8' }}>—</span>
                                )}
                              </td>
                              <td className="admin-editor-cell">
                                {dtype === 'number' ? (
                                  <input
                                    type="number"
                                    value={readNum('max_number', effective.max_number)}
                                    onChange={(event) => updateParamDraft(item, 'max_number', event.target.value)}
                                    style={inputStyle(hasDraft('max_number'), maxNumberError, { width: 90 })}
                                  />
                                ) : (
                                  <span style={{ color: '#94a3b8' }}>—</span>
                                )}
                              </td>
                              <td className="admin-editor-cell">
                                <input
                                  type="checkbox"
                                  checked={readBool('nullable', effective.nullable)}
                                  onChange={(event) => updateParamDraft(item, 'nullable', event.target.checked)}
                                  style={checkboxStyle(hasDraft('nullable'), nullableError)}
                                />
                              </td>
                              <td className="admin-editor-cell">
                                {isDepthAlgorithmParam ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {DEPTH_ALGORITHM_OPTIONS.map((algo) => (
                                      <label
                                        key={algo.value}
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={normalizedAllowedValues.includes(algo.value)}
                                          onChange={(event) => {
                                            const currentValues = readList(
                                              'allowed_values',
                                              effective.allowed_values || [],
                                              false
                                            ).map((v) => String(v));
                                            const valueSet = new Set(currentValues);
                                            if (event.target.checked) {
                                              valueSet.add(algo.value);
                                            } else {
                                              valueSet.delete(algo.value);
                                            }
                                            const orderedKnown = DEPTH_ALGORITHM_OPTIONS
                                              .map((entry) => entry.value)
                                              .filter((entry) => valueSet.has(entry));
                                            const extras = currentValues.filter(
                                              (entry) => !knownDepthAlgorithmSet.has(entry) && valueSet.has(entry)
                                            );
                                            updateParamDraft(item, 'allowed_values', [...orderedKnown, ...extras]);
                                          }}
                                          style={checkboxStyle(
                                            hasDraft('allowed_values'),
                                            allowedValuesError,
                                          )}
                                        />
                                        <span>{algo.label}</span>
                                      </label>
                                    ))}
                                    {depthAlgorithmExtraValues.length > 0 && (
                                      <TokenEditor
                                        tokens={depthAlgorithmExtraValues}
                                        numeric={false}
                                        placeholder="Extra algorithm"
                                        touched={hasDraft('allowed_values') || !!allowedValuesError}
                                        hasError={!!allowedValuesError}
                                        onChange={(values) => {
                                          const knownSelected = DEPTH_ALGORITHM_OPTIONS
                                            .map((entry) => entry.value)
                                            .filter((entry) => normalizedAllowedValues.includes(entry));
                                          updateParamDraft(item, 'allowed_values', [
                                            ...knownSelected,
                                            ...values.map((v) => String(v)),
                                          ]);
                                        }}
                                      />
                                    )}
                                  </div>
                                ) : (dtype === 'select' || dtype.startsWith('array')) ? (
                                  <TokenEditor
                                    tokens={readList('allowed_values', effective.allowed_values || [], isNumericAllowedList)}
                                    numeric={isNumericAllowedList}
                                    placeholder="Add allowed value"
                                    touched={hasDraft('allowed_values') || !!allowedValuesError}
                                    hasError={!!allowedValuesError}
                                    onChange={(values) => updateParamDraft(item, 'allowed_values', values)}
                                  />
                                ) : (
                                  <span style={{ color: '#94a3b8' }}>—</span>
                                )}
                              </td>
                              <td className="admin-editor-cell admin-editor-cell-status">
                                <div style={{ color: dirty ? '#b45309' : '#64748b', fontSize: 12 }}>
                                  {dirty ? 'Unsaved (touched)' : 'Saved'}
                                </div>
                                {rowErr.map((msg, idx) => (
                                  <div key={idx} style={{ color: '#b91c1c', fontSize: 11 }}>{msg}</div>
                                ))}
                              </td>
                              <td className="admin-editor-cell admin-editor-cell-actions">
                                <button
                                  type="button"
                                  disabled={!dirty}
                                  onClick={() => discardParamRow(item.key)}
                                  style={ui.btn({ variant: 'default' })}
                                >
                                  Discard Row
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {activeTab === 'crystal' && (
        <section className="admin-crystal-shell">
          <header className="admin-crystal-header">
            <div>
              <h3>Crystal Shape and Size Profiles</h3>
              <p>
                Manage crystal presets used as canvas reference overlays and 3D handoff size prefills.
              </p>
            </div>
            <div className="admin-crystal-actions">
              <button
                type="button"
                onClick={loadCrystalConfig}
                disabled={crystalLoading || crystalSaving}
                style={ui.btn({ variant: 'default' })}
              >
                {crystalLoading ? 'Loading…' : 'Reload'}
              </button>
              <button
                type="button"
                onClick={saveCrystalCatalog}
                disabled={crystalSaving || crystalLoading || !crystalCatalogDirty}
                style={ui.btn({ variant: 'primary' })}
              >
                {crystalSaving ? 'Saving…' : crystalCatalogDirty ? 'Save Crystal Profiles' : 'Saved'}
              </button>
            </div>
          </header>

          {crystalConfigUpdatedAt && (
            <p className="admin-crystal-meta">
              Last updated: {new Date(crystalConfigUpdatedAt).toLocaleString()}
            </p>
          )}
          {crystalError && <p className="admin-crystal-message admin-crystal-message--error">{crystalError}</p>}
          {crystalResult && <p className="admin-crystal-message admin-crystal-message--success">{crystalResult}</p>}

          {crystalLoading ? (
            <p className="admin-crystal-meta">Loading crystal profile catalog…</p>
          ) : (
            <div className="admin-crystal-mode-panel">
              <div className="admin-crystal-mode-selector" role="tablist" aria-label="Crystal profile modes">
                {crystalModeSections.map(({ mode, selectorLabel }) => (
                  <button
                    key={mode}
                    type="button"
                    role="tab"
                    aria-selected={activeCrystalModeSection?.mode === mode}
                    onClick={() => setActiveCrystalMode(mode)}
                    disabled={crystalSaving}
                    className={`admin-crystal-mode-selector-btn${activeCrystalModeSection?.mode === mode ? ' is-active' : ''}`}
                  >
                    {selectorLabel}
                  </button>
                ))}
              </div>

              {activeCrystalModeSection && activeCrystalSection ? (
                <article className="admin-crystal-mode-card">
                  <div className="admin-crystal-mode-head">
                    <h4>{activeCrystalModeSection.title}</h4>
                    <button
                      type="button"
                      onClick={() => addCrystalPreset(activeCrystalModeSection.mode)}
                      disabled={crystalSaving}
                      style={ui.btn({ variant: 'default' })}
                    >
                      Add Preset
                    </button>
                  </div>

                  <label className="admin-crystal-default">
                    <span>Default Preset</span>
                    <select
                      value={activeCrystalSection.defaultPresetId}
                      onChange={(event) => setCrystalDefaultPreset(activeCrystalModeSection.mode, event.target.value)}
                      disabled={crystalSaving}
                    >
                      {activeCrystalSection.presets.map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.label || preset.id}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="admin-crystal-table-wrap">
                    <table className="admin-crystal-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Label</th>
                          <th>Shape PNG</th>
                          <th>Clip Mask PNG</th>
                          <th>Ratio</th>
                          <th>Width (mm)</th>
                          <th>Height (mm)</th>
                          {activeCrystalModeSection.depth && <th>Depth (mm)</th>}
                          <th>DPI</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeCrystalSection.presets.map((preset, index) => (
                          <tr key={`${activeCrystalModeSection.mode}-${preset.id}-${index}`}>
                            <td>
                              <input
                                type="text"
                                value={preset.id}
                                onChange={(event) => updateCrystalPresetField(activeCrystalModeSection.mode, index, 'id', event.target.value)}
                                disabled={crystalSaving}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={preset.label}
                                onChange={(event) => updateCrystalPresetField(activeCrystalModeSection.mode, index, 'label', event.target.value)}
                                disabled={crystalSaving}
                              />
                            </td>
                            <td>
                              <div className="admin-crystal-shape-cell">
                                <div className="admin-crystal-shape-preview">
                                  {preset.shapePngDataUrl ? (
                                    <img
                                      src={preset.shapePngDataUrl}
                                      alt="Shape preview"
                                      className="admin-crystal-shape-image"
                                    />
                                  ) : (
                                    <span className="admin-crystal-shape-empty">No shape</span>
                                  )}
                                </div>
                                <input
                                  type="file"
                                  accept="image/png"
                                  onChange={(event) => {
                                    const file = event.target.files?.[0] || null;
                                    void uploadCrystalPresetShape(activeCrystalModeSection.mode, index, file);
                                    event.target.value = '';
                                  }}
                                  disabled={crystalSaving}
                                />
                                {preset.shapePngDataUrl && (
                                  <button
                                    type="button"
                                    style={ui.btn({ variant: 'default' })}
                                    onClick={() => clearCrystalPresetShape(activeCrystalModeSection.mode, index)}
                                    disabled={crystalSaving}
                                  >
                                    Remove Shape
                                  </button>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="admin-crystal-shape-cell">
                                <div className="admin-crystal-shape-preview">
                                  {preset.clipMaskPngDataUrl ? (
                                    <img
                                      src={preset.clipMaskPngDataUrl}
                                      alt="Clip mask preview"
                                      className="admin-crystal-shape-image"
                                    />
                                  ) : (
                                    <span className="admin-crystal-shape-empty">No mask</span>
                                  )}
                                </div>
                                <input
                                  type="file"
                                  accept="image/png"
                                  onChange={(event) => {
                                    const file = event.target.files?.[0] || null;
                                    void uploadCrystalPresetClipMask(activeCrystalModeSection.mode, index, file);
                                    event.target.value = '';
                                  }}
                                  disabled={crystalSaving}
                                />
                                {preset.clipMaskPngDataUrl && (
                                  <button
                                    type="button"
                                    style={ui.btn({ variant: 'default' })}
                                    onClick={() => clearCrystalPresetClipMask(activeCrystalModeSection.mode, index)}
                                    disabled={crystalSaving}
                                  >
                                    Remove Mask
                                  </button>
                                )}
                              </div>
                            </td>
                            <td>
                              {Number.isFinite(Number(preset.shapeRatio))
                                ? Number(preset.shapeRatio).toFixed(3)
                                : '—'}
                            </td>
                            <td>
                              <input
                                type="number"
                                min="1"
                                step="0.1"
                                value={preset.widthMm}
                                onChange={(event) => updateCrystalPresetDimension(activeCrystalModeSection.mode, index, 'width', event.target.value)}
                                disabled={crystalSaving}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                min="1"
                                step="0.1"
                                value={preset.heightMm}
                                onChange={(event) => updateCrystalPresetDimension(activeCrystalModeSection.mode, index, 'height', event.target.value)}
                                disabled={crystalSaving}
                              />
                            </td>
                            {activeCrystalModeSection.depth && (
                              <td>
                                <input
                                  type="number"
                                  min="1"
                                  step="0.1"
                                  value={preset.depthMm}
                                  onChange={(event) => updateCrystalPresetDepth(activeCrystalModeSection.mode, index, event.target.value)}
                                  disabled={crystalSaving}
                                />
                              </td>
                            )}
                            <td>
                              <input
                                type="number"
                                min="72"
                                max="1200"
                                step="10"
                                value={preset.dpi}
                                onChange={(event) => updateCrystalPresetDpi(activeCrystalModeSection.mode, index, event.target.value)}
                                disabled={crystalSaving}
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                onClick={() => removeCrystalPreset(activeCrystalModeSection.mode, index)}
                                disabled={crystalSaving}
                                style={ui.btn({ variant: 'default' })}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </article>
              ) : (
                <p className="admin-crystal-meta">No crystal mode configuration available.</p>
              )}
            </div>
          )}
        </section>
      )}

      {activeTab === 'diagnostics' && (
        <section style={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8, padding: 16 }}>
          <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Materialized (DB)</h3>
            <button type="button" onClick={() => refreshMaterialized(selectedTier)} disabled={!selectedTier || diagnosticsLoading} style={ui.btn({ variant: 'default' })}>
              {diagnosticsLoading ? 'Loading…' : 'Refresh'}
            </button>
          </header>
          <pre style={{ maxHeight: 360, overflow: 'auto', background: '#f8fafc', padding: 12, borderRadius: 8, marginTop: 10 }}>
            {dbMat ? JSON.stringify(dbMat.limits ?? dbMat, null, 2) : 'No data'}
          </pre>
        </section>
      )}

      {activeTab === 'metrics' && (
        <>
          <section style={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>System Health</h3>
              <button type="button" onClick={loadMetrics} disabled={metricsLoading} style={ui.btn({ variant: 'default' })}>
                {metricsLoading ? 'Loading…' : 'Refresh'}
              </button>
            </header>
            {metricsError && <p style={{ color: '#b91c1c' }}>{metricsError}</p>}
            {metricsData.fetchedAt && <p style={{ color: '#64748b' }}>Last updated: {new Date(metricsData.fetchedAt).toLocaleString()}</p>}

            {(() => {
              const health = metricsData.health || {};
              const components = health.components || {};
              const dbHealth = components.database || {};
              const systemHealth = components.system || {};
              const appHealth = components.application || {};
              const dbPool = dbHealth.connection_pool || {};
              const systemWarnings = Array.isArray(systemHealth.warnings) ? systemHealth.warnings : [];
              const appWarnings = Array.isArray(appHealth.warnings) ? appHealth.warnings : [];

              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                  <MetricsCard title="Overall" status={health.status}>
                    <MetricRow label="Timestamp" value={health.timestamp ? new Date(health.timestamp).toLocaleString() : '—'} />
                    <MetricRow label="Database" value={dbHealth.status || '—'} />
                    <MetricRow label="System" value={systemHealth.status || '—'} />
                    <MetricRow label="Application" value={appHealth.status || '—'} />
                  </MetricsCard>

                  <MetricsCard title="Database Health" status={dbHealth.status}>
                    <MetricRow label="Response" value={Number.isFinite(dbHealth.response_time_ms) ? `${dbHealth.response_time_ms} ms` : '—'} />
                    <MetricRow label="Pool Size" value={Number.isFinite(dbPool.size) ? dbPool.size : '—'} />
                    <MetricRow label="Checked Out" value={Number.isFinite(dbPool.checked_out) ? dbPool.checked_out : '—'} />
                    <MetricRow label="Overflow" value={Number.isFinite(dbPool.overflow) ? dbPool.overflow : '—'} />
                  </MetricsCard>

                  <MetricsCard title="System Health" status={systemHealth.status}>
                    <MetricRow label="CPU" value={Number.isFinite(systemHealth.cpu_percent) ? `${systemHealth.cpu_percent}%` : '—'} />
                    <MetricRow
                      label="Memory Used"
                      value={
                        systemHealth.memory && Number.isFinite(systemHealth.memory.used_percent)
                          ? `${systemHealth.memory.used_percent}%`
                          : '—'
                      }
                    />
                    <MetricRow
                      label="Disk Used"
                      value={
                        systemHealth.disk && Number.isFinite(systemHealth.disk.used_percent)
                          ? `${systemHealth.disk.used_percent}%`
                          : '—'
                      }
                    />
                    <MetricRow label="Warnings" value={systemWarnings.length ? systemWarnings.join('; ') : 'None'} />
                  </MetricsCard>

                  <MetricsCard title="Application Health" status={appHealth.status}>
                    <MetricRow label="Stuck Jobs" value={Number.isFinite(appHealth.stuck_jobs) ? appHealth.stuck_jobs : '—'} />
                    <MetricRow label="Recent Errors" value={Number.isFinite(appHealth.recent_errors) ? appHealth.recent_errors : '—'} />
                    <MetricRow label="Warnings" value={appWarnings.length ? appWarnings.join('; ') : 'None'} />
                  </MetricsCard>
                </div>
              );
            })()}
          </section>

          <section style={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8, padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>Performance Metrics</h3>
            {(() => {
              const performance = metricsData.performance || {};
              const system = performance.system || {};
              const db = performance.database || {};
              const app = performance.application || {};
              const jobsByStatus = app.jobs_by_status || {};
              const activity = app.activity || {};
              const dbPool = db.connection_pool || {};
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                  <MetricsCard title="System Performance">
                    <MetricRow label="CPU Cores" value={Number.isFinite(system.cpu?.count) ? system.cpu.count : '—'} />
                    <MetricRow label="CPU Usage" value={Number.isFinite(system.cpu?.percent) ? `${system.cpu.percent}%` : '—'} />
                    <MetricRow label="Memory Usage" value={Number.isFinite(system.memory?.percent) ? `${system.memory.percent}%` : '—'} />
                    <MetricRow label="Swap Usage" value={Number.isFinite(system.swap?.percent) ? `${system.swap.percent}%` : '—'} />
                  </MetricsCard>

                  <MetricsCard title="Database Performance">
                    <MetricRow label="DB Size" value={Number.isFinite(db.size_bytes) && db.size_bytes > 0 ? formatBytes(db.size_bytes) : '—'} />
                    <MetricRow label="Pool Size" value={Number.isFinite(dbPool.size) ? dbPool.size : '—'} />
                    <MetricRow label="Checked Out" value={Number.isFinite(dbPool.checked_out) ? dbPool.checked_out : '—'} />
                    <MetricRow label="Overflow" value={Number.isFinite(dbPool.overflow) ? dbPool.overflow : '—'} />
                  </MetricsCard>

                  <MetricsCard title="Job Activity">
                    <MetricRow label="Jobs Last Hour" value={Number.isFinite(activity.jobs_last_hour) ? activity.jobs_last_hour : '—'} />
                    <MetricRow label="Jobs Last Day" value={Number.isFinite(activity.jobs_last_day) ? activity.jobs_last_day : '—'} />
                    {Object.keys(jobsByStatus).length ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {Object.entries(jobsByStatus).map(([status, count]) => (
                          <MetricRow key={status} label={status} value={Number.isFinite(count) ? count : '—'} />
                        ))}
                      </div>
                    ) : (
                      <MetricRow label="Jobs By Status" value="—" />
                    )}
                  </MetricsCard>

                  <MetricsCard title="IO Activity">
                    <MetricRow label="Disk Read" value={formatBytes(system.disk_io?.read_bytes)} />
                    <MetricRow label="Disk Write" value={formatBytes(system.disk_io?.write_bytes)} />
                    <MetricRow label="Net Sent" value={formatBytes(system.network_io?.bytes_sent)} />
                    <MetricRow label="Net Recv" value={formatBytes(system.network_io?.bytes_recv)} />
                  </MetricsCard>
                </div>
              );
            })()}
          </section>
        </>
      )}

      {activeTab === 'jobs' && (
        <section style={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8, padding: 16 }}>
          <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>Jobs By User</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search username/email"
                value={jobsMetricsSearch}
                onChange={(event) => {
                  setJobsMetricsSearch(event.target.value);
                  setJobsMetricsPage(1);
                }}
                style={{ padding: 6 }}
              />
              <select
                value={jobsMetricsTierFilter}
                onChange={(event) => {
                  setJobsMetricsTierFilter(event.target.value);
                  setJobsMetricsPage(1);
                }}
              >
                <option value="">All tiers</option>
                <option value="free">free</option>
                <option value="basic">basic</option>
                <option value="premium">premium</option>
                <option value="admin">admin</option>
              </select>
              <select
                value={jobsMetricsStatusFilter}
                onChange={(event) => {
                  setJobsMetricsStatusFilter(event.target.value);
                  setJobsMetricsPage(1);
                }}
              >
                <option value="all">All statuses</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                value={jobsMetricsActiveOnly}
                onChange={(event) => {
                  setJobsMetricsActiveOnly(event.target.value);
                  setJobsMetricsPage(1);
                }}
              >
                <option value="">All users</option>
                <option value="active">Active only</option>
                <option value="inactive">Inactive only</option>
              </select>
              <button type="button" onClick={loadJobMetrics} disabled={jobsMetricsLoading} style={ui.btn({ variant: 'default' })}>
                {jobsMetricsLoading ? 'Loading…' : 'Refresh'}
              </button>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#334155', fontSize: 12 }}>
                Stale processing hours
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={jobsStaleHours}
                  onChange={(event) => setJobsStaleHours(event.target.value)}
                  style={{ width: 86, padding: 6 }}
                />
              </label>
            </div>
          </header>

          {jobsMetricsError && <p style={{ color: '#b91c1c' }}>{jobsMetricsError}</p>}
          {jobsMetricsData.fetchedAt && (
            <p style={{ color: '#64748b' }}>
              Last updated: {new Date(jobsMetricsData.fetchedAt).toLocaleString()}
            </p>
          )}

          <div style={{ overflow: 'auto', marginTop: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={ui.th}>User</th>
                  <th style={ui.th}>Tier</th>
                  <th style={ui.th}>Total</th>
                  <th style={ui.th}>Pending</th>
                  <th style={ui.th}>Processing</th>
                  <th style={ui.th}>Completed</th>
                  <th style={ui.th}>Failed</th>
                  <th style={ui.th}>Cancelled</th>
                  <th style={ui.th}>Filtered Count</th>
                  <th style={ui.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(jobsMetricsData.users || []).map((entry) => {
                  const counts = entry.counts || {};
                  const pendingCount = Number.isFinite(counts.pending) ? counts.pending : 0;
                  const processingCount = Number.isFinite(counts.processing) ? counts.processing : 0;
                  const failedCount = Number.isFinite(counts.failed) ? counts.failed : 0;
                  const clearPendingKey = `${entry.user_id}:pending`;
                  const clearFailedKey = `${entry.user_id}:failed`;
                  const clearBothKey = `${entry.user_id}:pending,failed`;
                  const clearStaleProcessingKey = `${entry.user_id}:processing:${jobsStaleHoursValue}`;
                  const isClearingPending = jobsClearingKey === clearPendingKey;
                  const isClearingStaleProcessing = jobsClearingKey === clearStaleProcessingKey;
                  const isClearingFailed = jobsClearingKey === clearFailedKey;
                  const isClearingBoth = jobsClearingKey === clearBothKey;
                  const isBusy = Boolean(jobsClearingKey);

                  return (
                    <tr key={entry.user_id}>
                      <td style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <strong>{entry.username}</strong>
                          <span style={{ fontSize: 12, color: '#64748b' }}>{entry.email}</span>
                        </div>
                      </td>
                      <td style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>{entry.tier}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>{counts.total ?? 0}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>{pendingCount}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>{processingCount}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>{counts.completed ?? 0}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>{failedCount}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>{counts.cancelled ?? 0}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>
                        <strong>{entry.filtered_count ?? 0}</strong>
                      </td>
                      <td style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            style={ui.btn({ variant: 'default' })}
                            disabled={isBusy || pendingCount === 0}
                            onClick={() => onClearUserJobs(entry.user_id, ['pending'], 'pending')}
                          >
                            {isClearingPending ? 'Clearing…' : `Clear Pending (${pendingCount})`}
                          </button>
                          <button
                            type="button"
                            style={ui.btn({ variant: 'default' })}
                            disabled={isBusy || processingCount === 0}
                            onClick={() => onClearUserStaleProcessingJobs(entry.user_id)}
                          >
                            {isClearingStaleProcessing
                              ? 'Clearing…'
                              : `Clear Processing > ${jobsStaleHoursValue}h`}
                          </button>
                          <button
                            type="button"
                            style={ui.btn({ variant: 'default' })}
                            disabled={isBusy || failedCount === 0}
                            onClick={() => onClearUserJobs(entry.user_id, ['failed'], 'failed')}
                          >
                            {isClearingFailed ? 'Clearing…' : `Clear Failed (${failedCount})`}
                          </button>
                          <button
                            type="button"
                            style={ui.btn({ variant: 'default' })}
                            disabled={isBusy || (pendingCount + failedCount) === 0}
                            onClick={() => onClearUserJobs(entry.user_id, ['pending', 'failed'], 'pending and failed')}
                          >
                            {isClearingBoth ? 'Clearing…' : `Clear Both (${pendingCount + failedCount})`}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!jobsMetricsLoading && (jobsMetricsData.users || []).length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ padding: 12, color: '#64748b' }}>
                      No users found for current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {Number(jobsMetricsData?.pagination?.total_pages || 0) > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                style={ui.btn({ variant: 'default' })}
                onClick={() => setJobsMetricsPage((prev) => Math.max(1, prev - 1))}
                disabled={jobsMetricsLoading || Number(jobsMetricsData?.pagination?.page || 1) <= 1}
              >
                Previous
              </button>
              <span style={{ color: '#64748b', fontSize: 13 }}>
                Page {jobsMetricsData?.pagination?.page || 1} of {jobsMetricsData?.pagination?.total_pages || 1}
                {' '}
                ({jobsMetricsData?.pagination?.total_count || 0} users)
              </span>
              <button
                type="button"
                style={ui.btn({ variant: 'default' })}
                onClick={() =>
                  setJobsMetricsPage((prev) =>
                    Math.min(Number(jobsMetricsData?.pagination?.total_pages || 1), prev + 1)
                  )
                }
                disabled={
                  jobsMetricsLoading ||
                  Number(jobsMetricsData?.pagination?.page || 1) >= Number(jobsMetricsData?.pagination?.total_pages || 1)
                }
              >
                Next
              </button>
            </div>
          )}
        </section>
      )}

      {activeTab === 'users' && (
        <section style={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8, padding: 16 }}>
          <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>Users</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search username/email"
                value={userSearch}
                onChange={(event) => setUserSearch(event.target.value)}
                style={{ padding: 6 }}
              />
              <select value={userTierFilter} onChange={(event) => setUserTierFilter(event.target.value)}>
                <option value="">All tiers</option>
                <option value="free">free</option>
                <option value="basic">basic</option>
                <option value="premium">premium</option>
                <option value="admin">admin</option>
              </select>
              <button type="button" onClick={loadUsers} disabled={usersLoading} style={ui.btn({ variant: 'default' })}>
                {usersLoading ? 'Loading…' : 'Refresh Users'}
              </button>
            </div>
          </header>

          <div style={{ display: 'flex', gap: 10, marginTop: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="New username"
              value={newUser.username}
              onChange={(event) => setNewUser({ ...newUser, username: event.target.value })}
            />
            <input
              type="email"
              placeholder="New email"
              value={newUser.email}
              onChange={(event) => setNewUser({ ...newUser, email: event.target.value })}
            />
            <input
              type="password"
              placeholder="Temp password"
              value={newUser.password}
              onChange={(event) => setNewUser({ ...newUser, password: event.target.value })}
            />
            <button type="button" onClick={onCreateUser} disabled={usersLoading} style={ui.btn({ variant: 'primary' })}>
              Create
            </button>
          </div>

          <div style={{ maxHeight: 420, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={ui.th}>Username</th>
                  <th style={ui.th}>Email</th>
                  <th style={ui.th}>Tier</th>
                  <th style={ui.th}>Active</th>
                  <th style={ui.th}>Admin</th>
                  <th style={ui.th}>Last Login</th>
                  <th style={ui.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((entry) => {
                  const draftTier = userTierDrafts[entry.id] ?? entry.tier ?? 'free';
                  const unchanged = String(draftTier).toLowerCase() === String(entry.tier || 'free').toLowerCase();
                  const busy = userSavingId === entry.id;

                  return (
                    <tr key={entry.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: 8 }}>{entry.username}</td>
                      <td style={{ padding: 8 }}>{entry.email}</td>
                      <td style={{ padding: 8 }}>
                        <select
                          value={draftTier}
                          onChange={(event) =>
                            setUserTierDrafts((prev) => ({
                              ...prev,
                              [entry.id]: event.target.value,
                            }))
                          }
                        >
                          {['free', 'basic', 'premium', 'admin'].map((tier) => (
                            <option key={tier} value={tier}>{tier}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => onUpdateUserTier(entry.id, draftTier)}
                          disabled={busy || unchanged}
                          style={{ marginLeft: 6, ...ui.btn({ variant: 'default' }) }}
                        >
                          Set
                        </button>
                      </td>
                      <td style={{ padding: 8 }}>{entry.is_active ? 'Yes' : 'No'}</td>
                      <td style={{ padding: 8 }}>{entry.is_admin ? 'Yes' : 'No'}</td>
                      <td style={{ padding: 8 }}>{entry.last_login ? new Date(entry.last_login).toLocaleString() : '-'}</td>
                      <td style={{ padding: 8 }}>
                        {entry.is_active ? (
                          <button type="button" onClick={() => onDeactivateUser(entry.id)} disabled={busy} style={ui.btn({ variant: 'default' })}>
                            Deactivate
                          </button>
                        ) : (
                          <button type="button" onClick={() => onReactivateUser(entry.id)} disabled={busy} style={ui.btn({ variant: 'default' })}>
                            Reactivate
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onUnlockUser(entry.id)}
                          disabled={busy}
                          style={{ marginLeft: 6, ...ui.btn({ variant: 'default' }) }}
                        >
                          Unlock
                        </button>
                        <button
                          type="button"
                          onClick={() => onRevokeSessions(entry.id)}
                          disabled={busy}
                          style={{ marginLeft: 6, ...ui.btn({ variant: 'default' }) }}
                        >
                          Revoke Sessions
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default AdminConsole;
